import { UuidT } from "../flatbuffers/generated/front-end/uuid";
import { U128T } from "../flatbuffers/generated/u128/u128";

export const ZERO_UUID_STRING = "00000000-0000-0000-0000-000000000000";

export const EMPTY_UUID = new UuidT(new U128T(BigInt(0), BigInt(0)));

/**
 * Renders a UUID as its canonical dashed string.
 *
 * Accepts a plain string as well as a `UuidT`, because replay files and older
 * data carry the string form.
 */
export function uuidToString(uuid: UuidT | string | null): string {
  // Handle plain string UUIDs (from replay files / legacy data)
  if (typeof uuid === "string") return uuid || ZERO_UUID_STRING;
  if (!uuid?.value) return ZERO_UUID_STRING;

  // FlatBuffer U128T stores upper/lower as signed bigint. Convert to
  // unsigned 64-bit so the hex output matches the canonical UUID string.
  // Without this, negative values (high bit set) produce "-1a2b..." instead
  // of the correct "e5d4..." hex — causing state key mismatches.
  const TWO_64 = BigInt("18446744073709551616"); // 2^64
  const toUnsigned64 = (v: bigint): bigint => (v < BigInt(0) ? v + TWO_64 : v);
  const high = toUnsigned64(BigInt(uuid.value.upper));
  const low = toUnsigned64(BigInt(uuid.value.lower));

  const hex = `${high.toString(16).padStart(16, "0")}${low.toString(16).padStart(16, "0")}`;

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(
    16,
    20,
  )}-${hex.slice(20)}`;
}

/**
 * Parses a dashed UUID string into the generated `UuidT`. An empty string yields the nil UUID.
 */
export function stringToUuidT(uuid: string): UuidT {
  if (!uuid) return EMPTY_UUID;
  const hex = uuid.replace(/-/g, "");
  if (hex.length < 32) return EMPTY_UUID;
  const upper = BigInt(`0x${hex.slice(0, 16)}`);
  const lower = BigInt(`0x${hex.slice(16, 32)}`);
  return new UuidT(new U128T(upper, lower));
}

/**
 * Compares two UUIDs, in either representation.
 *
 * The mixed representations are the reason this exists: comparing a `UuidT`
 * against a string with `===` is always false and fails silently.
 */
export function uuidsEqual(a: UuidT | string | null, b: UuidT | string | null): boolean {
  if (a === b) return true;
  const ua = typeof a === "string" ? stringToUuidT(a) : a;
  const ub = typeof b === "string" ? stringToUuidT(b) : b;
  if (!ua?.value || !ub?.value) return !ua?.value && !ub?.value;
  return ua.value.upper === ub.value.upper && ua.value.lower === ub.value.lower;
}
